import { mergeRefs } from "@react-aria/utils";
import cx from "classnames";
import { fromUnixTime } from "date-fns/esm";
import { useMemo, type JSX } from "react";

import { formatDate } from "utils/date";
import {
  Administrative,
  ArrowsLeftRight,
  CalendarCross,
  ChevronDown,
  CircleCrossed,
  ClockRewind,
  LinkUnlinked,
  Lock,
} from "components/icons/generated";
import Box from "ds/components/Box";
import { default as Button } from "ds/components/Button";
import DropdownMenu from "ds/components/DropdownMenu";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tooltip from "ds/components/Tooltip";
import useElementWidth from "hooks/useElementWidth";
import useTypedContext from "hooks/useTypedContext";
import { BillingTierFeature, Stack } from "types/generated";
import { truncate } from "utils/strings";
import { SubscriptionContext } from "views/Account/SubscriptionWrapper";
import { AccountContext } from "views/AccountWrapper";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import TableCellEmpty from "ds/components/Table/Row/CellEmpty";
import IconWithTooltip from "components/IconWithTooltip";

import styles from "./styles.module.css";

const INITIAL_MULTIPLE_ITEMS_DROPDOWN_WIDTH = 54;
const SPACE = 4;
const ITEM_SIZE = 24;

const StacksTableSettingsCell = ({ item: stack, ...cellProps }: TableSimpleCellProps<Stack>) => {
  const { viewer } = useTypedContext(AccountContext);
  const { tierFeatures } = useTypedContext(SubscriptionContext);

  const items = useMemo(() => {
    const nextDeleteSchedule = stack.scheduledDeletes.reduce(
      (acc, next) => {
        if (!acc && next.nextSchedule) {
          return next.nextSchedule;
        }

        if (next.nextSchedule && acc) {
          return Math.min(acc, next.nextSchedule);
        }

        return acc;
      },
      undefined as number | undefined
    );

    return [
      stack.vcsDetached && (
        <IconWithTooltip
          key="key.stack.vcsDetached"
          icon={LinkUnlinked}
          tooltip="Source code integration has been detached"
          className={styles.settingsIcon}
          size="large"
        />
      ),
      stack.administrative && (
        <IconWithTooltip
          key="key.stack.administrative"
          icon={Administrative}
          tooltip="Administrative"
          className={styles.settingsIcon}
          size="large"
        />
      ),
      tierFeatures.includes(BillingTierFeature.DriftDetection) &&
        stack.integrations?.driftDetection?.nextSchedule && (
          <IconWithTooltip
            key="key.stack.driftDetection"
            icon={ArrowsLeftRight}
            tooltip={`Drift detection on. Next schedule: ${formatDate({
              date: fromUnixTime(stack.integrations?.driftDetection?.nextSchedule || 0),
            })}`}
            className={styles.settingsIcon}
            size="large"
          />
        ),
      nextDeleteSchedule && (
        <IconWithTooltip
          key="key.stack.nextDeleteSchedule"
          icon={CalendarCross}
          tooltip={`Stack scheduled for deletion on: ${formatDate({
            date: fromUnixTime(nextDeleteSchedule),
          })}`}
          className={styles.settingsIcon}
          size="large"
        />
      ),
      stack.isStateRollback && stack.managesStateFile && (
        <IconWithTooltip
          key="key.stack.isStateRollback"
          icon={ClockRewind}
          tooltip="State rolled back"
          className={styles.settingsIcon}
          size="large"
        />
      ),
      stack.isDisabled && (
        <IconWithTooltip
          key="key.stack.isDisabled"
          icon={CircleCrossed}
          tooltip="Stack is disabled"
          className={styles.settingsIcon}
          size="large"
        />
      ),
      !!stack.lockedBy && (
        <IconWithTooltip
          key="key.stack.lockedBy"
          tooltip={`Stack was locked by ${stack.lockedBy === viewer?.id ? "You" : stack.lockedBy}${
            stack.lockedAt
              ? ` on ${formatDate({
                  date: fromUnixTime(stack.lockedAt),
                })}${stack.lockNote ? `: ${truncate(stack.lockNote, 150)}` : ""}`
              : ""
          }`}
          icon={Lock}
          className={styles.settingsIcon}
          size="large"
        />
      ),
    ].filter((v) => !!v);
  }, [
    stack.administrative,
    stack.integrations?.driftDetection?.nextSchedule,
    stack.isDisabled,
    stack.isStateRollback,
    stack.lockNote,
    stack.lockedAt,
    stack.lockedBy,
    stack.managesStateFile,
    stack.scheduledDeletes,
    stack.vcsDetached,
    tierFeatures,
    viewer?.id,
  ]);

  const [containerRef, containerSize] = useElementWidth<HTMLDivElement>();
  const [multipleTagsDropdownRef, multipleTagsDropdownSize] = useElementWidth<HTMLButtonElement>(
    INITIAL_MULTIPLE_ITEMS_DROPDOWN_WIDTH
  );

  const hiddenItems = useMemo(() => {
    if (!items.length) {
      return [];
    }

    const hiddenItems: JSX.Element[] = [];

    let sumOfSizes = multipleTagsDropdownSize;

    for (let i = 0; i < items.length; i++) {
      const size = ITEM_SIZE + SPACE;
      const isLast = i === items.length - 1;

      sumOfSizes += size;

      if (isLast) {
        sumOfSizes -= multipleTagsDropdownSize;
      }

      if (containerSize && sumOfSizes > containerSize) {
        hiddenItems.push(...items.slice(i));
        return hiddenItems;
      }
    }

    return hiddenItems;
  }, [containerSize, items, multipleTagsDropdownSize]);

  if (items.length === 0) {
    return (
      <TableSimpleCell {...cellProps}>
        <TableCellEmpty />
      </TableSimpleCell>
    );
  }

  if (items.length === 1) {
    return <TableSimpleCell {...cellProps}>{items[0]}</TableSimpleCell>;
  }

  const moreTagsTooltip = `${hiddenItems.length === items.length ? `${hiddenItems.length} items` : `+${hiddenItems.length}`}`;

  return (
    <TableSimpleCell {...cellProps}>
      <Box ref={containerRef} fullWidth justify="start" align="center" gap="small">
        {items.filter((item) => !hiddenItems.includes(item)).map((item) => item)}
        {!!hiddenItems.length && (
          <DropdownMenu
            placement="bottom right"
            className={styles.settingsDropdownSection}
            triggerComponent={
              <Tooltip
                on={({ ref, ...tooltipProps }) => (
                  <Button
                    {...tooltipProps}
                    ref={mergeRefs<HTMLButtonElement>(ref, multipleTagsDropdownRef)}
                    aria-label="Show more items"
                    className={cx(tooltipProps.className)}
                    variant="secondary"
                    size="small"
                    endIcon={ChevronDown}
                    withTextEllipsis
                  >
                    {moreTagsTooltip}
                  </Button>
                )}
              >
                Show more items
              </Tooltip>
            }
          >
            {hiddenItems.map((item, i) => (
              <DropdownMenuItem className={styles.settingsDropdownItem} key={i}>
                {item}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        )}
      </Box>
    </TableSimpleCell>
  );
};

export default StacksTableSettingsCell;
